import { useState, useRef, useEffect, useCallback } from "react";
import {
  CreatePodcastFromReferenceRequestDtoGenerationType,
  PodcastTranscriptItem,
} from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
//
import {
  Play,
  Square,
  SkipBack,
  SkipForward,
  Download,
  FileText,
  Users,
  Wand2,
  Search,
} from "lucide-react";
import ImageOrVideo from "@features/tools/components/tool_components/ImageOrVideo";
import PodcastPlayerModal from "./PodcastPlayerModal";
import { usePodcastPlayer } from "../context/PodcastPlayerContext";
import PodcastAssignmentModal from "./PodcastAssignmentModal";

//

interface CustomAudioProps {
  id?: number;
  url?: string;
  name?: string;
  transcript?: PodcastTranscriptItem[] | undefined;
  createdAt?: string;
  imageUrl?: string;
  videoUrl?: string;
  isImageLoading?: boolean;
  podcastGenerationType?: CreatePodcastFromReferenceRequestDtoGenerationType;
  onRegenClick?: (
    podcastId: number | undefined,
    podcastGenerationType: CreatePodcastFromReferenceRequestDtoGenerationType,
  ) => void;
  author?: string;
  openPdfCustomizationModal?: (podcastId: number | undefined) => void;
  currentlyPlayingId?: number | null;
  onPlayStateChange?: (id: number | null) => void;
  hideEditButton?: boolean;
}

const PodcastAudioCard = ({
  id,
  url,
  name,
  transcript,
  createdAt,
  imageUrl,
  videoUrl,
  isImageLoading,
  author,
  podcastGenerationType,
  onRegenClick,
  openPdfCustomizationModal,
  hideEditButton,
}: CustomAudioProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { t } = useTranslation();

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalInitialTimeMs, setModalInitialTimeMs] = useState<
    number | undefined
  >(undefined);
  const [modalAutoPlay, setModalAutoPlay] = useState<boolean>(false);
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);

  useEffect(() => {
    const closeHandler = () => setIsModalOpen(false);
    window.addEventListener("podcast:close-player", closeHandler);
    return () =>
      window.removeEventListener("podcast:close-player", closeHandler);
  }, []);

  const {
    currentPodcastId: ctxCurrentId,
    isPlaying: ctxIsPlaying,
    currentTimeMs: ctxTimeMs,
    durationMs: ctxDurationMs,
    play: ctxPlay,
    togglePlayPause: ctxTogglePlayPause,
    seek: ctxSeek,
  } = usePodcastPlayer();

  const handlePlayPause = useCallback(() => {
    if (!id) return;
    if (ctxCurrentId === id) {
      // If this is the current podcast, just toggle play/pause
      ctxTogglePlayPause();
    } else {
      // If it's a different podcast, start playing this one
      ctxPlay({ id, url }, 0);
    }
  }, [id, url, ctxCurrentId, ctxPlay, ctxTogglePlayPause]);

  const handleMp3Download = useCallback(() => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name || "podcast"}.mp3`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  }, [url, name]);

  const handleSkipBackward = useCallback(() => {
    if (ctxCurrentId !== id) return; // Only skip if this is the current podcast
    const newTime = Math.max(0, ctxTimeMs - 10000);
    ctxSeek(newTime);
  }, [ctxCurrentId, id, ctxTimeMs, ctxSeek]);

  const handleSkipForward = useCallback(() => {
    if (ctxCurrentId !== id) return; // Only skip if this is the current podcast
    const newTime = Math.min(ctxDurationMs || 0, ctxTimeMs + 10000);
    ctxSeek(newTime);
  }, [ctxCurrentId, id, ctxTimeMs, ctxDurationMs, ctxSeek]);

  // Only show progress for the currently playing podcast
  const isCurrentlyPlaying = ctxCurrentId === id && ctxIsPlaying;
  const isCurrentPodcast = ctxCurrentId === id;
  const displayTimeMs = isCurrentPodcast ? ctxTimeMs : 0;
  const displayDurationMs = isCurrentPodcast ? ctxDurationMs : 0;
  const progressPercentage =
    displayDurationMs > 0 ? (displayTimeMs / displayDurationMs) * 100 : 0;

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Check if the click target is not a button or its child element
      const target = e.target as HTMLElement;
      if (target.closest("button")) {
        return; // Don't open modal if clicking on any button
      }

      // When opening modal, sync playback state and position
      if (audioRef.current) {
        const t = audioRef.current.currentTime || 0;
        setModalInitialTimeMs(t * 1000);
        setModalAutoPlay(isPlaying || t > 0);
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
      setIsModalOpen(true);
    },
    [isPlaying],
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden w-full mx-auto">
      <div onClick={handleCardClick} className="cursor-pointer">
        {/* Media area with all overlays */}
        <div className="relative aspect-w-16 aspect-h-9 w-full">
          <ImageOrVideo
            imageUrl={imageUrl}
            videoUrl={videoUrl}
            isLoading={isImageLoading}
            topic={name}
            resolution={"original"}
            createdAt={createdAt}
          />

          {/* Top title overlay */}
          {name && (
            <div className="absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
              <span className="inline-flex max-w-[70%] items-center text-white/90 text-xs sm:text-sm md:text-base px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 shadow-sm">
                <span className="truncate" title={name}>
                  {name}
                </span>
              </span>
            </div>
          )}

          {/* Top right - View Details Button */}
          <button
            onClick={() => {
              // When opening modal, sync playback state and position
              if (audioRef.current) {
                const t = audioRef.current.currentTime || 0;
                setModalInitialTimeMs(t * 1000);
                setModalAutoPlay(isPlaying || t > 0);
                if (isPlaying) {
                  audioRef.current.pause();
                  setIsPlaying(false);
                }
              }
              setIsModalOpen(true);
            }}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20 shadow-sm z-10 flex items-center gap-1"
            aria-label={t("common.details")}
          >
            <Search className="w-4 h-4" />
            {t("podcast.transcript")}
          </button>

          {/* Bottom Progress Bar and Controls Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
            {/* Progress Bar - Only show detailed progress for current podcast */}
            <div className="mb-3">
              <div
                className={`h-1.5 bg-white/30 rounded-full relative group ${
                  isCurrentPodcast ? "cursor-pointer" : "cursor-default"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isCurrentPodcast) return; // Only allow seeking for current podcast
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const percentage = clickX / rect.width;
                  const newTime = percentage * (displayDurationMs || 0);
                  ctxSeek(newTime);
                }}
              >
                <div
                  className="h-full bg-white/90 rounded-full relative transition-all"
                  style={{ width: `${progressPercentage}%` }}
                >
                  {isCurrentPodcast && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm" />
                  )}
                </div>
              </div>
              {isCurrentPodcast && (
                <div className="flex justify-between mt-1 text-xs text-white/70">
                  <span>
                    {Math.floor(displayTimeMs / 60000)}:
                    {String(
                      Math.floor((displayTimeMs % 60000) / 1000),
                    ).padStart(2, "0")}
                  </span>
                  <span>
                    {Math.floor((displayDurationMs || 0) / 60000)}:
                    {String(
                      Math.floor(((displayDurationMs || 0) % 60000) / 1000),
                    ).padStart(2, "0")}
                  </span>
                </div>
              )}
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSkipBackward();
                }}
                disabled={!isCurrentPodcast}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20 disabled:opacity-30"
                aria-label="Skip back 10 seconds"
              >
                <SkipBack className="w-4 h-4" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
                disabled={!url}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20 disabled:opacity-50"
                aria-label={isCurrentlyPlaying ? "Pause" : "Play"}
              >
                {isCurrentlyPlaying ? (
                  <Square className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 translate-x-[1px]" />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSkipForward();
                }}
                disabled={!isCurrentPodcast}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20 disabled:opacity-30"
                aria-label="Skip forward 10 seconds"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-2">
              {/* Left: Download Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMp3Download();
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20"
                >
                  <Download className="w-3 h-3" />
                  MP3
                </button>
                {openPdfCustomizationModal && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openPdfCustomizationModal(id);
                    }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20"
                  >
                    <FileText className="w-3 h-3" />
                    PDF
                  </button>
                )}
              </div>

              {/* Right: Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAssignModal(true);
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20"
                >
                  <Users className="w-3 h-3" />
                  <span className="hidden md:inline">
                    {t("podcast.assignToStudents")}
                  </span>
                  <span className="md:hidden">Assign</span>
                </button>
                {onRegenClick &&
                  id &&
                  podcastGenerationType &&
                  !hideEditButton && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRegenClick(id, podcastGenerationType);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20"
                    >
                      <Wand2 className="w-3 h-3" />
                      <span className="hidden md:inline">
                        {t("assignments.generator.preview.regenerate")}
                      </span>
                      <span className="md:hidden">{t("common.edit")}</span>
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <PodcastPlayerModal
          podcast={{
            id: id,
            title: name,
            authorName: author,
            imageUrl: imageUrl,
            url: url,
            transcript: transcript,
          }}
          onClose={() => setIsModalOpen(false)}
          onRegenClick={(pid, genType) => {
            if (onRegenClick && pid && genType) onRegenClick(pid, genType);
          }}
          podcastGenerationType={podcastGenerationType}
          openPdfCustomizationModal={openPdfCustomizationModal}
          initialTimeMs={modalInitialTimeMs}
          autoPlay={modalAutoPlay}
        />
      )}

      {/* Assignment Modal */}
      {showAssignModal && id && (
        <PodcastAssignmentModal
          podcastId={id}
          podcastTitle={name}
          show={showAssignModal}
          onClose={() => setShowAssignModal(false)}
        />
      )}
    </div>
  );
};

export default PodcastAudioCard;
